/**
 * Pure date helpers for {@link CalendarSheet}. Kept dependency-free (no
 * date-fns / luxon) so the component stays dual-target and light. All functions
 * are calendar-grid presentation helpers — no timezone or locale parsing.
 */

export type CalendarDay = {
    date: Date
    day: number
    /** True when the day belongs to the previous/next month (leading/trailing). */
    isOutsideMonth: boolean
}

/** 0 = Sunday, 1 = Monday — which column the week starts on. */
export type WeekStart = 0 | 1

export const startOfDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate())

export const isSameDay = (a?: Date | null, b?: Date | null): boolean =>
    Boolean(a && b && startOfDay(a).getTime() === startOfDay(b).getTime())

/** Inclusive range check on day granularity. Order of bounds does not matter. */
export const isWithinRange = (date: Date, start?: Date | null, end?: Date | null): boolean => {
    if (!start || !end) {
        return false
    }
    const time = startOfDay(date).getTime()
    const lo = Math.min(startOfDay(start).getTime(), startOfDay(end).getTime())
    const hi = Math.max(startOfDay(start).getTime(), startOfDay(end).getTime())
    return time >= lo && time <= hi
}

// Calendar-component arithmetic (not millisecond addition) so day stepping is
// DST-safe — e.g. crossing the late-October fall-back in Europe/Helsinki, which
// would otherwise drift a fixed 24h step and duplicate/skip a day.
export const addDays = (date: Date, days: number): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

/**
 * Builds a fixed 6×7 calendar matrix for the month containing `month`. Always
 * 42 cells (6 weeks) so the grid height never jumps between months — matching
 * the Figma sheet. Leading/trailing days are flagged `isOutsideMonth`.
 */
export const buildCalendarMatrix = (month: Date, weekStartsOn: WeekStart = 0): CalendarDay[][] => {
    const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1)
    const firstWeekday = firstOfMonth.getDay() // 0 = Sun
    const leading = (firstWeekday - weekStartsOn + 7) % 7
    const gridStart = addDays(firstOfMonth, -leading)

    const weeks: CalendarDay[][] = []
    for (let week = 0; week < 6; week++) {
        const days: CalendarDay[] = []
        for (let dow = 0; dow < 7; dow++) {
            const date = addDays(gridStart, week * 7 + dow)
            days.push({
                date,
                day: date.getDate(),
                isOutsideMonth: date.getMonth() !== month.getMonth(),
            })
        }
        weeks.push(days)
    }
    return weeks
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

/** `October 2020` — default month-title formatter (English, no i18n dependency). */
export const formatMonthTitle = (month: Date): string =>
    `${MONTHS[month.getMonth()]} ${month.getFullYear()}`

const WEEKDAYS_SUN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/** Two-letter weekday headers, rotated to honour `weekStartsOn`. */
export const weekdayLabels = (weekStartsOn: WeekStart = 0): string[] =>
    weekStartsOn === 1 ? [...WEEKDAYS_SUN.slice(1), WEEKDAYS_SUN[0]] : WEEKDAYS_SUN
